import React, { useState, useEffect, createContext, useRef, useCallback } from 'react';
import { initializeApp } from 'firebase/app';
import {
  getFirestore,
  doc,
  setDoc,
  getDoc,
  collection,
  query,
  orderBy,
  limit,
  getDocs,
  updateDoc
} from 'firebase/firestore';

export const FirebaseContext = createContext(null);
export const AuthContext = createContext(null);

const firebaseConfig = {
    apiKey: "AIzaSyDWHx9rWVoXxvGF05Y4ITL6spkwC_phOpE",
    authDomain: "connectun-2051b.firebaseapp.com",
    projectId: "connectun-2051b",
    storageBucket: "connectun-2051b.firebasestorage.app",
    messagingSenderId: "80336030752",
    appId: "1:80336030752:web:e0e355c40bd24f6e5d0045"
};

const app = initializeApp(firebaseConfig);
const db = getFirestore(app);
let auth = null;
let googleProvider = null;
let authModulePromise = null;
let authInitPromise = null;

const loadAuthModule = async () => {
  if (!authModulePromise) {
    authModulePromise = import('firebase/auth');
  }

  return authModulePromise;
};

const ensureAuthInitialized = async () => {
  const authModule = await loadAuthModule();

  if (!authInitPromise) {
    auth = authModule.getAuth(app);
    googleProvider = new authModule.GoogleAuthProvider();
    authInitPromise = authModule.setPersistence(auth, authModule.browserLocalPersistence).then(() => ({
      auth,
      googleProvider,
      authModule
    }));
  }

  return authInitPromise;
};

const GameWrapper = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProgress, setUserProgress] = useState(null);
  const [loading, setLoading] = useState(false);
  const [authError, setAuthError] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const authSubscriptionRef = useRef(null);
  const authBootedRef = useRef(false);

  const fetchLeaderboard = useCallback(async () => {
    const q = query(
      collection(db, 'users'),
      orderBy('gamesCompleted', 'desc'),
      limit(10)
    );
    await getDocs(q);
    // Leaderboard data is fetched but not stored in state as it's not used in this component
  }, []);

  const initializeAuthSession = useCallback(async () => {
    const { auth: initializedAuth, authModule } = await ensureAuthInitialized();

    if (authBootedRef.current) {
      return initializedAuth;
    }

    authBootedRef.current = true;
    setLoading(true);

    authSubscriptionRef.current = authModule.onAuthStateChanged(initializedAuth, async (firebaseUser) => {
      if (firebaseUser) {
        setUser(firebaseUser);
        const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
        if (userDoc.exists()) {
          setUserProgress(userDoc.data());
        } else {
          const initialProgress = {
            gamesCompleted: 0,
            currentStreak: 0,
            bestStreak: 0,
            completedGames: [],
            lastCompletedGame: null,
            dailyCompletions: {},
            weeklyCompletions: {},
            displayName: firebaseUser.displayName || firebaseUser.email?.split('@')[0],
            photoURL: firebaseUser.photoURL || null
          };
          await setDoc(doc(db, 'users', firebaseUser.uid), initialProgress);
          setUserProgress(initialProgress);
        }
        fetchLeaderboard();
        setShowAuthModal(false);
      } else {
        setUser(null);
        setUserProgress(null);
      }
      setLoading(false);
    });

    return initializedAuth;
  }, [fetchLeaderboard]);


  useEffect(() => {
    const loginBtn = document.getElementById('login-button');
    if (loginBtn) {
        const handleLoginClick = () => {
            setShowAuthModal(true);
            initializeAuthSession();
        };

        const updateLoginButton = () => {
            if (user) {
                loginBtn.style.display = 'none';
            } else {
                loginBtn.style.display = 'block';
                loginBtn.addEventListener('click', handleLoginClick);
            }
        };
        
        updateLoginButton();
        
        // Clean up
        return () => {
            loginBtn.removeEventListener('click', handleLoginClick);
        };
    }
  }, [initializeAuthSession, user]); // Add user as dependency to update button state when user logs in/out

  // Format display name function for leaderboard entries
  const formatDisplayName = (name) => {
    if (!name) return 'Anonymous Player';
    
    // If it looks like an email, don't display it
    if (name.includes('@')) return 'Player';
    
    // If name has a space (first and last name), format as NameS
    if (name.includes(' ')) {
      const nameParts = name.split(' ');
      const firstName = nameParts[0];
      const lastName = nameParts[nameParts.length - 1];
      return `${firstName}${lastName.charAt(0)}`;
    }
    
    return name;
  };

  const markGameAsPlayed = async (gameId) => {
    if (!user) return;
  
    try {
      const userRef = doc(db, 'users', user.uid);
      // First get the current user data
      const userDoc = await getDoc(userRef);
      const currentData = userDoc.data();
      
      const updatedData = {
        ...currentData,
        markedAsPlayed: [...(currentData.markedAsPlayed || []), gameId]
      };
  
      await updateDoc(userRef, updatedData);
      setUserProgress(updatedData);
    } catch (error) {
      console.error('Error marking game as played:', error);
    }
  };

  useEffect(() => {
    return () => {
      authSubscriptionRef.current?.();
    };
  }, []);

  // Auth methods
  const loginWithGoogle = async () => {
    try {
      setAuthError(null);
      const { authModule } = await ensureAuthInitialized();
      const initializedAuth = await initializeAuthSession();
      const result = await authModule.signInWithPopup(initializedAuth, googleProvider);
      setUser(result.user);
      setShowAuthModal(false);
    } catch (error) {
      setAuthError(error.message);
    }
  };

  const loginWithEmail = async (email, password) => {
    try {
        setAuthError(null);
        const { authModule } = await ensureAuthInitialized();
        const initializedAuth = await initializeAuthSession();
        await authModule.signInWithEmailAndPassword(initializedAuth, email, password);
        setShowAuthModal(false);
    } catch (error) {
        console.error('Login error:', error);
        // Translate Firebase errors to user-friendly messages
        switch (error.code) {
            case 'auth/user-not-found':
                setAuthError('No account found with this email');
                break;
            case 'auth/wrong-password':
                setAuthError('Incorrect password');
                break;
            case 'auth/invalid-email':
                setAuthError('Invalid email address');
                break;
            case 'auth/too-many-requests':
                setAuthError('Too many attempts. Please try again later');
                break;
            default:
                setAuthError('Error signing in. Please try again');
        }
    }
  };

  const signUpWithEmail = async (email, password, displayName) => {
    try {
        setAuthError(null);
        if (!displayName) {
            setAuthError('Display name is required for signup');
            return;
        }
        const { authModule } = await ensureAuthInitialized();
        const initializedAuth = await initializeAuthSession();
        const result = await authModule.createUserWithEmailAndPassword(initializedAuth, email, password);
        const initialProgress = {
            gamesCompleted: 0,
            currentStreak: 0,
            bestStreak: 0,
            completedGames: [],
            lastCompletedGame: null,
            dailyCompletions: {},
            weeklyCompletions: {},
            displayName,
            photoURL: null
        };
        await setDoc(doc(db, 'users', result.user.uid), initialProgress);
        setShowAuthModal(false);
    } catch (error) {
        console.error('Signup error:', error);
        // Translate Firebase errors to user-friendly messages
        switch (error.code) {
            case 'auth/email-already-in-use':
                setAuthError('An account with this email already exists');
                break;
            case 'auth/invalid-email':
                setAuthError('Invalid email address');
                break;
            case 'auth/operation-not-allowed':
                setAuthError('Email/password sign up is not enabled');
                break;
            case 'auth/weak-password':
                setAuthError('Password is too weak - please use at least 6 characters');
                break;
            default:
                setAuthError('Error creating account. Please try again');
        }
    }
  };

  const logout = async () => {
    try {
      const { authModule } = await ensureAuthInitialized();
      const initializedAuth = await initializeAuthSession();
      await authModule.signOut(initializedAuth);
      setUser(null);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Game completion handling with leaderboard updates
  // Enhanced completeGame function with better error handling and sequential updates
const completeGame = async (gameId, isSuccess) => {
  if (!user) return;
  
  try {
    console.log(`Starting game completion process for game ${gameId}, success: ${isSuccess}`);
    const userRef = doc(db, 'users', user.uid);
    const now = new Date();
    const todayStr = now.toISOString().split('T')[0]; // YYYY-MM-DD format
    
    // Log the date information for debugging
    console.log(`Current date: ${now.toLocaleString()}`);
    console.log(`Today string: ${todayStr}`);
    
    // Get the ISO week format (YYYY-Wnn)
    const getWeekId = (date) => {
      const d = new Date(date);
      d.setHours(0, 0, 0, 0);
      d.setDate(d.getDate() + 3 - (d.getDay() + 6) % 7);
      const week = Math.floor((d.getTime() - new Date(d.getFullYear(), 0, 4).getTime()) / 86400000 / 7) + 1;
      return `${d.getFullYear()}-W${week.toString().padStart(2, '0')}`;
    };
    const weekId = getWeekId(now);
    console.log(`Week ID: ${weekId}`);
    
    // Get current user data
    const userDoc = await getDoc(userRef);
    if (!userDoc.exists()) {
      console.error("User document doesn't exist");
      return;
    }
    
    const userData = userDoc.data();
    console.log("User data retrieved:", JSON.stringify(userData.dailyCompletions || {}, null, 2));
    
    if (isSuccess) {
      // Update user document
      const dailyCompletions = userData.dailyCompletions || {};
      dailyCompletions[todayStr] = (dailyCompletions[todayStr] || 0) + 1;
      
      const weeklyCompletions = userData.weeklyCompletions || {};
      weeklyCompletions[weekId] = (weeklyCompletions[weekId] || 0) + 1;
      
      console.log(`Daily completions for ${todayStr}: ${dailyCompletions[todayStr]}`);
      console.log(`Weekly completions for ${weekId}: ${weeklyCompletions[weekId]}`);
      
      const updatedUserData = {
        gamesCompleted: (userData.gamesCompleted || 0) + 1,
        completedGames: [...(userData.completedGames || []), parseInt(gameId)],
        currentStreak: (userData.currentStreak || 0) + 1,
        bestStreak: Math.max((userData.bestStreak || 0), (userData.currentStreak || 0) + 1),
        lastCompletedGame: now.toISOString(),
        dailyCompletions,
        weeklyCompletions
      };
      
      console.log("Updating user document with new data");
      await updateDoc(userRef, updatedUserData);
      setUserProgress({...userData, ...updatedUserData});
      console.log("User document updated successfully");
      
      // Now update leaderboards sequentially to avoid race conditions
      try {
        console.log("Updating daily leaderboard...");
        await updateLeaderboard(userRef.id, userData.displayName, todayStr, dailyCompletions[todayStr]);
        console.log("Daily leaderboard updated successfully");
      } catch (dailyError) {
        console.error("Error updating daily leaderboard:", dailyError);
        // Try once more for daily
        try {
          console.log("Retrying daily leaderboard update...");
          await updateLeaderboard(userRef.id, userData.displayName, todayStr, dailyCompletions[todayStr]);
          console.log("Daily leaderboard retry successful");
        } catch (retryError) {
          console.error("Daily leaderboard retry failed:", retryError);
        }
      }
      
      try {
        console.log("Updating weekly leaderboard...");
        await updateLeaderboard(userRef.id, userData.displayName, weekId, weeklyCompletions[weekId], true);
        console.log("Weekly leaderboard updated successfully");
      } catch (weeklyError) {
        console.error("Error updating weekly leaderboard:", weeklyError);
        // Try once more for weekly
        try {
          console.log("Retrying weekly leaderboard update...");
          await updateLeaderboard(userRef.id, userData.displayName, weekId, weeklyCompletions[weekId], true);
          console.log("Weekly leaderboard retry successful");
        } catch (retryError) {
          console.error("Weekly leaderboard retry failed:", retryError);
        }
      }
      
      try {
        console.log("Updating all-time leaderboard...");
        await updateAllTimeLeaderboard(userRef.id, userData.displayName, updatedUserData.gamesCompleted);
        console.log("All-time leaderboard updated successfully");
      } catch (allTimeError) {
        console.error("Error updating all-time leaderboard:", allTimeError);
      }
      
      // Fetch updated leaderboard
      console.log("Fetching updated leaderboard data");
      fetchLeaderboard();
    } else {
      // Reset streak on failure
      console.log("Game failed, resetting streak");
      await updateDoc(userRef, {
        currentStreak: 0
      });
      setUserProgress({...userData, currentStreak: 0});
      console.log("Streak reset successfully");
    }
  } catch (error) {
    console.error('Error updating game progress:', error);
  }
};

  // Helper function to update a daily or weekly leaderboard
  // Enhanced updateLeaderboard function with better error handling and logging
const updateLeaderboard = async (userId, displayName, timeId, score, isWeekly = false) => {
  try {
    console.log(`Updating ${isWeekly ? 'weekly' : 'daily'} leaderboard for ${timeId} with score ${score}`);
    
    const formattedName = formatDisplayName(displayName);
    const docId = `${isWeekly ? 'weekly_' : 'daily_'}${timeId}`;
    console.log(`Document ID: ${docId}`);
    
    const leaderboardRef = doc(db, 'Leaderboards', docId);
    const leaderboardDoc = await getDoc(leaderboardRef);
    
    if (leaderboardDoc.exists()) {
      console.log(`${isWeekly ? 'Weekly' : 'Daily'} leaderboard exists, updating...`);
      // Update existing leaderboard
      const data = leaderboardDoc.data();
      let leaders = data.leaders || [];
      const existingIndex = leaders.findIndex(leader => leader.userId === userId);
      
      if (existingIndex >= 0) {
        // Update existing entry
        console.log(`User found at index ${existingIndex}, updating score from ${leaders[existingIndex].score} to ${score}`);
        leaders[existingIndex].score = score;
        leaders[existingIndex].displayName = formattedName; // Update name in case it changed
      } else {
        // Add new entry
        console.log(`User not found in leaders, adding new entry`);
        leaders.push({ userId, displayName: formattedName, score });
      }
      
      // Sort by score descending and limit to top 100
      leaders.sort((a, b) => b.score - a.score);
      if (leaders.length > 100) {
        leaders = leaders.slice(0, 100);
      }
      
      console.log(`Updating leaderboard with ${leaders.length} entries`);
      await updateDoc(leaderboardRef, { leaders });
      console.log(`${isWeekly ? 'Weekly' : 'Daily'} leaderboard updated successfully`);
    } else {
      // Create new leaderboard
      console.log(`${isWeekly ? 'Weekly' : 'Daily'} leaderboard does not exist, creating new one`);
      await setDoc(leaderboardRef, {
        [isWeekly ? 'weekId' : 'date']: timeId,
        leaders: [{ userId, displayName: formattedName, score }]
      });
      console.log(`New ${isWeekly ? 'weekly' : 'daily'} leaderboard created successfully`);
    }
  } catch (error) {
    console.error(`Error updating ${isWeekly ? 'weekly' : 'daily'} leaderboard:`, error);
    throw error; // Re-throw to allow caller to handle
  }
};

  // Helper function for all-time leaderboard
  const updateAllTimeLeaderboard = async (userId, displayName, score) => {
    try {
      const formattedName = formatDisplayName(displayName);
      const docRef = doc(db, 'Leaderboards', 'alltime');
      const leaderboardDoc = await getDoc(docRef);
      
      if (leaderboardDoc.exists()) {
        const data = leaderboardDoc.data();
        let leaders = data.leaders || [];
        const existingIndex = leaders.findIndex(leader => leader.userId === userId);
        
        if (existingIndex >= 0) {
          leaders[existingIndex].score = score;
          leaders[existingIndex].displayName = formattedName; // Update name in case it changed
        } else {
          leaders.push({ userId, displayName: formattedName, score });
        }
        
        // Sort and limit
        leaders.sort((a, b) => b.score - a.score);
        if (leaders.length > 100) {
          leaders = leaders.slice(0, 100);
        }
        
        await updateDoc(docRef, { 
          leaders, 
          lastUpdated: new Date().toISOString()
        });
      } else {
        await setDoc(docRef, {
          lastUpdated: new Date().toISOString(),
          leaders: [{ userId, displayName: formattedName, score }]
        });
      }
    } catch (error) {
      console.error('Error updating all-time leaderboard:', error);
    }
  };

  const AuthModal = () => {
    const [showEmailForm, setShowEmailForm] = useState(false);
  
    return (
      <div className="modals-container">
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>Sign In / Sign Up</h2>
              <button className="close-btn" onClick={() => setShowAuthModal(false)}>×</button>
            </div>
            <div className="modal-content">
              <h3>Gain free access to extra features!</h3>
              
              <div className="features-list">
                <div className="feature-item">
                  <span className="feature-icon">✓</span>
                  Track streaks and stats
                </div>
                <div className="feature-item">
                  <span className="feature-icon">✓</span>
                  Save your progress
                </div>
                <div className="feature-item">
                  <span className="feature-icon">✓</span>
                  More coming soon!
                </div>
              </div>

              {loading && (
                <div className="auth-loading">
                  Loading sign-in...
                </div>
              )}
  
              {!showEmailForm ? (
                <div className="auth-buttons">
                  <button 
                    onClick={loginWithGoogle} 
                    className="auth-btn google"
                  >
                    <img src="https://www.google.com/favicon.ico" alt="Google" />
                    Sign In with Google
                  </button>
                  
                  <button 
                    onClick={() => setShowEmailForm(true)} 
                    className="auth-btn email"
                  >
                    <span className="email-icon">✉</span>
                    Sign In with Email
                  </button>
  
                  <div className="coming-soon">
                    More options coming soon!
                  </div>
                </div>
              ) : (
                <div className="email-form">
                    {authError && (
                        <div className="error-message" style={{
                            color: '#dc3545',
                            backgroundColor: '#f8d7da',
                            padding: '10px',
                            borderRadius: '4px',
                            marginBottom: '10px',
                            border: '1px solid #dc3545'
                        }}>
                            {authError}
                        </div>
                    )}
                  <form onSubmit={e => {
                    e.preventDefault();
                    const formData = new FormData(e.target);
                    if (e.nativeEvent.submitter.name === 'signup') {
                      signUpWithEmail(
                        formData.get('email'),
                        formData.get('password'),
                        formData.get('displayName')
                      );
                    } else {
                      loginWithEmail(formData.get('email'), formData.get('password'));
                    }
                  }}>
                    <input
                      type="email"
                      placeholder="Email"
                      name="email"
                      required
                    />
                    <input
                      type="password"
                      placeholder="Password"
                      name="password"
                      required
                    />
                    <input
                      type="text"
                      placeholder="Display Name (for signup)"
                      name="displayName"
                    />
                    <div className="button-group">
                      <button type="submit" name="login" className="auth-btn">Login</button>
                      <button type="submit" name="signup" className="auth-btn">Sign Up</button>
                    </div>
                    <button 
                      type="button" 
                      className="back-btn"
                      onClick={() => setShowEmailForm(false)}
                    >
                      ← Back to options
                    </button>
                  </form>
                </div>
              )}
            </div>
          </div>
  
          <style>{`
            .features-list {
              margin: 20px 0;
            }
            
            .modals-container h2, .modals-container h3{
                margin:0;
                padding:0;
            }

            .feature-item {
              margin: 10px 0;
              font-size: 1.1em;
            }
  
            .feature-icon {
              color: #4CAF50;
              margin-right: 10px;
            }
  
            .auth-buttons {
              display: flex;
              flex-direction: column;
              gap: 10px;
              margin-top: 30px;
            }
  
            .auth-btn {
              display: flex;
              align-items: center;
              justify-content: center;
              padding: 12px;
              border: 1px solid #ddd;
              border-radius: 8px;
              cursor: pointer;
              font-size: 1.1em;
              background: white;
              color: #333;
              transition: background 0.3s;
              width: 100%;
            }
  
            .auth-btn:not(:disabled):hover {
              background: #f5f5f5;
            }
  
            .auth-btn img, .email-icon {
              width: 20px;
              height: 20px;
              margin-right: 10px;
            }
  
            .coming-soon {
              text-align: center;
              margin-top: 10px;
              color: #666;
            }
  
            .email-form {
              margin-top: 20px;
            }
  
            .email-form input {
              width: 100%;
              padding: 12px;
              margin-bottom: 10px;
              border: 1px solid #ddd;
              border-radius: 8px;
              font-size: 1em;
            }
  
            .button-group {
              display: flex;
              gap: 10px;
              margin-bottom: 10px;
            }
  
            .back-btn {
              width: 100%;
              padding: 12px;
              border: none;
              background: none;
              color: #666;
              cursor: pointer;
              font-size: 1em;
            }
  
            .back-btn:hover {
              color: #333;
            }
          `}</style>
        </div>
      </div>
    );
  };

  return (
    <FirebaseContext.Provider value={{ db }}>
      <AuthContext.Provider value={{ 
        user, 
        userProgress, 
        login: () => setShowAuthModal(true), 
        logout,
        completeGame,
        markGameAsPlayed
      }}>
        <div className="game-container">
          
            {user ? (
            <nav className="auth-nav">
              <div className="user-info">
                
                <span>{userProgress?.displayName}</span>
                <div className="stats">
                  <span>🏆 Completed: {userProgress?.gamesCompleted || 0}</span>
                  <span>🔥 Streak: {userProgress?.currentStreak || 0}</span>
                </div>
                <button onClick={logout} className="auth-btn">Logout</button>
              </div>
              </nav>
            ) : (
              <></>
            )}
          
          {children}

          {showAuthModal && <AuthModal />}

          
        </div>
      </AuthContext.Provider>
    </FirebaseContext.Provider>
  );
};

export default GameWrapper;
